import type {
  Action,
  BlockNode,
  ExperienceDocument,
  LayoutSnapshot,
  Viewport,
} from '@personalizehq/schemas/ir';
import { Ctx, expand, layout, resolve } from '@personalizehq/schemas/measure';
import { loadFonts } from './fonts.js';
import { makeMeasureText } from './measure-text.js';
import { applyScrollOffsets, paint } from './render.js';
import { domSnapshot } from './snapshot.js';
import { createSurface, type SurfaceHandle } from './surfaces.js';

// The production web renderer. It POSITIONS FROM THE SOLVER'S COMPUTED RECTS — the identical
// architecture to the iOS/Android/Flutter renderers — and uses CSS for painting only. The
// CSS-flow implementation that delegates layout to the browser lives in spike/web, where it
// serves as the independent cross-check and browser drift breaks CI instead of a customer.
//
// NO ZOD, by import discipline: only the zod-free `/ir` and `/measure` subpaths of
// @personalizehq/schemas. (Zod still lands in node_modules via that dependency — that is fine
// and expected; the point is that none of it is in this bundle.)

export type { Action, ExperienceDocument, LayoutSnapshot, Viewport };

export interface CustomApi {
  dismiss(): void;
  track(name: string, properties?: Record<string, string>): void;
}

/** The host's painter for a `custom` block. It receives a FIXED rect and contributes NOTHING to
 *  layout (SPEC §8) — we never ask it how big it wants to be. Return a cleanup if it needs one. */
export type CustomComponent = (
  el: HTMLElement,
  config: unknown,
  api: CustomApi
) => (() => void) | void;

export interface MountOptions {
  container?: HTMLElement;
  registry?: Record<string, CustomComponent>;
  capabilities?: string[];
  bindings?: Record<string, string>;
  /** Items a `repeat` expands over, KEYED BY COLLECTION (SPEC §7.2) — the same shape the native
   *  `setItems(key, items)` will take. It was a flat array, which meant every `repeat` in a
   *  document read the same list and `source.collection` keyed on nothing. */
  items?: Record<string, Array<Record<string, string>>>;
  assets?: Record<string, string>;
  onAction?: (action: Action, nodeId: string) => void;
  /** What a dismiss INTENT does. Default: destroy the mount — correct in an app, fatal in the
   *  editor, where Esc, a scrim click, a `{type:'dismiss'}` button and `CustomApi.dismiss()` would
   *  each delete the author's own canvas. They all funnel through this one seam, so an editor
   *  neutralises the lot by passing a no-op. Layout-neutral: the solver never sees it. */
  onDismiss?: () => void;
  viewport?: Partial<Viewport>;
  /** Default true. The conformance harness sets false: fixture media URLs are placeholders, and
   *  layout never depends on the bytes (`aspect_ratio` reserves the box). */
  loadMedia?: boolean | undefined;
  /** Test seam only: override the colour-scheme source. */
  matchMedia?: (q: string) => { matches: boolean; addEventListener?: MediaQueryList['addEventListener'] };
}

export interface MountHandle {
  /** Fonts register asynchronously and LAYOUT WAITS FOR THEM (SPEC §4.1) — measuring with a
   *  fallback font would move every wrap point. Nothing is painted until this resolves. */
  ready: Promise<void>;
  update(doc: ExperienceDocument, opts?: Partial<MountOptions>): void;
  /** The solver's frames — authoritative. */
  snapshot(): LayoutSnapshot;
  /** What the DOM actually did — the conformance signal. */
  domSnapshot(): LayoutSnapshot;
  /** Hit-test in snapshot coordinates. Editor-facing; rects-as-data, no DOM round-trip. */
  nodeAt(x: number, y: number): string | null;
  destroy(): void;
}

/** The web has NO OS font-scale API. Browser/user font-size preferences surface as a changed
 *  ROOT FONT SIZE (default 16px), so that is the only honest signal. Page ZOOM is invisible here
 *  and correctly so — zoom scales everything uniformly, including our points. */
function webFontScale(): number {
  const root = parseFloat(getComputedStyle(document.documentElement).fontSize);
  return Number.isFinite(root) && root > 0 ? root / 16 : 1;
}

function defaultViewport(opts: MountOptions, container: HTMLElement): Viewport {
  const mm = opts.matchMedia ?? ((q: string) => window.matchMedia(q));
  const box = container === document.body ? null : container.getBoundingClientRect();
  return {
    w: box?.width || window.innerWidth,
    h: box?.height || window.innerHeight,
    color_scheme: mm('(prefers-color-scheme: dark)').matches ? 'dark' : 'light',
    font_scale: webFontScale(),
    direction: document.documentElement.dir === 'rtl' ? 'rtl' : 'ltr',
    ...opts.viewport,
  };
}

export function mount(doc: ExperienceDocument, options: MountOptions = {}): MountHandle {
  let opts: MountOptions = { ...options };
  let document_ = doc;
  let surface: SurfaceHandle | null = null;
  let solver: LayoutSnapshot = { viewport: {} as Viewport, nodes: [] };
  let cleanups: Array<() => void> = [];
  let destroyed = false;
  // The font invariant is enforced HERE, not by promise sequencing outside: an OS dark-mode
  // flip or an early update() calls draw() directly, and painting before registration would
  // measure with a fallback font and move every wrap point.
  let fontsReady = false;
  const loadedSrcs = new Set<string>();
  let surfaceKind: string | null = null;

  const measureText = makeMeasureText();
  const container = opts.container ?? window.document.body;

  const dismiss = () => {
    if (opts.onDismiss) opts.onDismiss();
    else handle.destroy();
  };

  function renderCtx() {
    return {
      registry: new Set(Object.keys(opts.registry ?? {})),
      capabilities: new Set(opts.capabilities ?? []),
      bindings: opts.bindings ?? {},
      items: opts.items ?? {},
    };
  }

  function draw(): void {
    if (destroyed || !fontsReady) return;
    for (const c of cleanups) c();
    cleanups = [];

    const vp = defaultViewport(opts, container);
    const rctx = renderCtx();

    // The painter needs the RESOLVED TREE (block kinds, spans, actions, config) — a
    // LayoutSnapshot carries only frames. So run the pre-measure passes here, in the normative
    // order (SPEC §7: expand THEN resolve), and hand layout() the resolved document. layout()
    // re-runs both internally; they are idempotent on an already-processed tree, which keeps
    // exactly ONE normative entry point instead of forking the solver's internals.
    const tree: BlockNode | null = resolve(expand(document_.root, rctx), rctx);
    // A root that resolves to nothing (an unknown block with no fallback) lays out to nothing.
    solver = tree
      ? layout({ ...document_, root: tree }, vp, measureText, rctx)
      : { viewport: vp, nodes: [] };

    // The chrome is per-surface. An editor switching modal -> banner in the inspector must get
    // new chrome, not a banner's tree inside a modal's scrim.
    if (surface && surfaceKind !== document_.surface) {
      surface.destroy();
      surface = null;
    }
    if (!surface) {
      surface = createSurface(document_.surface, vp, container, dismiss);
      surfaceKind = document_.surface;
      // Unknown surface kind renders NOTHING (SPEC §11) — never a guessed modal.
      if (!surface) return;
    }
    // The screen the surface anchors against — the PINNED viewport, re-applied every frame because
    // a viewport toggle (device size, font scale) changes it without rebuilding the chrome.
    surface.root.style.width = `${vp.w}px`;
    surface.root.style.height = `${vp.h}px`;

    // The layer is viewport-WIDE (the solver already centered the root inside that width) but only
    // as TALL AS THE DOCUMENT — that is what gives the surface's vertical anchor something to
    // anchor. Sized from the ROOT'S OWN FRAME, so it cannot disagree with what was measured.
    surface.layer.style.width = `${vp.w}px`;
    surface.layer.style.height = `${solver.nodes[0]?.frame[3] ?? vp.h}px`;

    if (!tree) {
      surface.layer.replaceChildren();
      return;
    }

    const ctx = new Ctx(document_.theme, vp, measureText, rctx.bindings);
    const { root, customMounts } = paint(tree, solver, document_.theme, vp, ctx, {
      onAction: (a, id) => opts.onAction?.(a, id),
      dismiss,
      loadMedia: opts.loadMedia,
    });

    // ponytail: full re-render (replaceChildren), bounded by MAX_LAYOUT_NODES=1024 -> a few ms.
    // Keyed reuse by data-phq-id is the upgrade path IF the editor ever measures a problem;
    // it trades correctness hazards (stale styles, custom-component identity, scroll position)
    // for speed we do not yet need.
    surface.layer.replaceChildren(root);
    // F15: the sheet grabber is chrome, not part of the painted tree, so it must be re-added after
    // replaceChildren wipes the layer. It rides the layer's top edge (the sheet's top).
    if (surface.grabber) surface.layer.appendChild(surface.grabber);
    applyScrollOffsets(surface.layer);

    for (const m of customMounts) {
      const painter = opts.registry?.[m.identifier];
      if (!painter) continue; // resolve() already replaced unregistered customs with fallbacks
      // The api is PER-BLOCK: a track() from a custom component must carry ITS node id, or P4's
      // analytics cannot attribute the event to anything.
      const api: CustomApi = {
        dismiss,
        track: (name, properties) =>
          opts.onAction?.(
            properties ? { type: 'track_event', name, properties } : { type: 'track_event', name },
            m.nodeId
          ),
      };
      const cleanup = painter(m.el, m.config, api);
      if (cleanup) cleanups.push(cleanup);
    }
  }

  // Only subscribe when the scheme was DEFAULTED — a caller-pinned viewport is the caller's.
  let unsubscribeScheme: (() => void) | undefined;
  if (options.viewport?.color_scheme === undefined) {
    const mql = (opts.matchMedia ?? ((q: string) => window.matchMedia(q)))(
      '(prefers-color-scheme: dark)'
    ) as MediaQueryList;
    const onChange = () => draw();
    mql.addEventListener?.('change', onChange);
    unsubscribeScheme = () => mql.removeEventListener?.('change', onChange);
  }

  /** Registers any font `src` we have not seen yet. The editor — this API's primary consumer —
   *  adds font tokens mid-session; without this, a newly-referenced font would silently render
   *  in the fallback family FOREVER, with every wrap point wrong. */
  async function ensureFonts(): Promise<void> {
    const assets = opts.assets ?? {};
    const wanted = Object.values(document_.theme.fonts)
      .map((f) => f.src)
      .filter((src): src is string => !!src && !!assets[src] && !loadedSrcs.has(src));
    if (wanted.length === 0 && fontsReady) return;
    await loadFonts(document_.theme, assets);
    for (const src of wanted) loadedSrcs.add(src);
    fontsReady = true;
  }

  const ready = ensureFonts().then(() => {
    draw();
  });

  const handle: MountHandle = {
    ready,
    update(next, nextOpts) {
      document_ = next;
      if (nextOpts) opts = { ...opts, ...nextOpts };
      const unseen = Object.values(next.theme.fonts).some(
        (f) => f.src && (opts.assets ?? {})[f.src] && !loadedSrcs.has(f.src)
      );
      // Paint immediately with what is already registered; if the update brought NEW fonts,
      // register them and repaint. (A first paint in the old font beats a blank canvas in the
      // editor, and the repaint is what makes the wrap points right.)
      draw();
      if (unseen) void ensureFonts().then(draw);
    },
    snapshot: () => solver,
    domSnapshot: () =>
      surface
        ? domSnapshot(surface.layer, solver.viewport)
        : { viewport: solver.viewport, nodes: [] },
    nodeAt(x, y) {
      // REVERSE: last painted is topmost. Box children overlap by design (full-bleed hero +
      // overlaid text + floating CTA is the marquee layout), so a forward scan would return the
      // background every time.
      for (let i = solver.nodes.length - 1; i >= 0; i--) {
        const [nx, ny, nw, nh] = solver.nodes[i]!.frame;
        if (x >= nx && x <= nx + nw && y >= ny && y <= ny + nh) return solver.nodes[i]!.id;
      }
      return null;
    },
    destroy() {
      if (destroyed) return;
      destroyed = true;
      for (const c of cleanups) c();
      cleanups = [];
      unsubscribeScheme?.();
      surface?.destroy();
      surface = null;
    },
  };

  return handle;
}

export { assertTextAgreement, domSnapshot } from './snapshot.js';
export { makeMeasureText } from './measure-text.js';
